export const FOLLOW_UP_TITLE_MAX_LENGTH = 255;
export const FOLLOW_UP_DESCRIPTION_MAX_LENGTH = 1000;
// Server search kicks in only past this many characters (same gate as other searches in chat)
export const FOLLOW_UP_SEARCH_MIN_CHARS = 2;
export const FOLLOW_UP_SEARCH_DEBOUNCE_MS = 1000;

export const EMPTY_FOLLOW_UP_FORM = {
  title: '',
  description: '',
  dueAt: null, // ISO string
  relatedMessage: null,
};

export const getCustomerName = (selectedChat) => {
  const contactName = selectedChat?.primary_contact?.name?.trim();
  return contactName || selectedChat?.primary_contact?.wa_phone || selectedChat?.name || 'Customer';
};

// Follow-up times are always 24-hour (HH:mm) — the support team works in the Norwegian format.
// Built by hand so no browser locale can turn it into "24:05" or add AM/PM.
const pad2 = (value) => String(value).padStart(2, '0');

// "16:36"
export const formatTime24 = (dateOrIso) => {
  const date = dateOrIso instanceof Date ? dateOrIso : new Date(dateOrIso);
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

// "Oct 28, 2026, 16:36"
export const formatFollowUpDateTime = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  const datePart = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${datePart}, ${formatTime24(date)}`;
};

// "28 Oct 2026 • 17:52"
export const formatDueChip = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  const datePart = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${datePart} • ${formatTime24(date)}`;
};

// "Mon 15:20" within the last week, otherwise "16 Oct 15:20" (year added when not current)
export const formatRelatedMessageTime = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  const now = new Date();
  const time = formatTime24(date);
  const ageInDays = (now - date) / (24 * 60 * 60 * 1000);
  if (ageInDays >= 0 && ageInDays < 6) {
    return `${date.toLocaleDateString('en-US', { weekday: 'short' })} ${time}`;
  }
  const options = { day: 'numeric', month: 'short' };
  if (date.getFullYear() !== now.getFullYear()) options.year = 'numeric';
  return `${date.toLocaleDateString('en-GB', options)} ${time}`;
};

export const isFollowUpOverdue = (followUp) =>
  followUp?.status === 'pending' && !!followUp?.due_at && new Date(followUp.due_at) < new Date();

/* ------------------------------------------------------------------------------------------ */
/* Related message                                                                            */
/* ------------------------------------------------------------------------------------------ */

export const MESSAGE_TYPE_LABELS = {
  text: 'Message',
  image: 'Image',
  video: 'Video',
  voice: 'Voice message',
  sticker: 'Sticker',
  document: 'Document',
};

// Strip WhatsApp markdown markers / HTML so previews read as plain text.
const toPlainText = (value) =>
  String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/[*_~`]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const getFileName = (fileUrl) => {
  if (!fileUrl) return '';
  try {
    const last = String(fileUrl).split('?')[0].split('/').pop() || '';
    return decodeURIComponent(last);
  } catch {
    return '';
  }
};

const resolveMessageType = ({ mediaKind, mime, hasFile }) => {
  const kind = String(mediaKind || '').toLowerCase();
  const type = String(mime || '').toLowerCase();
  if (!kind && !type && !hasFile) return 'text';
  if (kind === 'sticker') return 'sticker';
  if (kind === 'image' || type.startsWith('image/')) return 'image';
  if (kind === 'video' || type.startsWith('video/')) return 'video';
  if (kind === 'audio' || kind === 'voice' || type.startsWith('audio/')) return 'voice';
  return 'document';
};


export const toRelatedMessage = (message, fallbackSenderName = null) => {
  if (!message || message.id == null) return null;

  // Mobile-shaped payloads (e.g. the restore endpoint) use camelCase and epoch-ms dates
  const senderType = message.sender_type ?? message.senderType;
  const createdAt = message.created_at ?? message.createdAt ?? message.timestamp;
  const attachment = Array.isArray(message.attachments) ? message.attachments[0] : null;
  const isCustomer = senderType ? senderType === 'Customer' : message.role === 'user';
  const file = message.file || attachment?.url;

  return {
    id: message.id,
    sender_type: senderType || (isCustomer ? 'Customer' : 'Agent'),
    is_customer: isCustomer,
    sender_name: message.sender_name || message.senderName || fallbackSenderName || null,
    timestamp: typeof createdAt === 'number' ? new Date(createdAt).toISOString() : createdAt || null,
    type: resolveMessageType({
      mediaKind: message.media_kind || message.mediaKind || attachment?.mediaKind,
      mime: message.media_mime || message.mediaMime || attachment?.type,
      hasFile: !!file,
    }),
    text: toPlainText(message.body ?? message.content),
    caption: toPlainText(message.caption || attachment?.caption),
    file_name: getFileName(file) || attachment?.name || '',
    duration: Number(message.duration ?? message.audio?.duration ?? attachment?.duration) || null,
    deleted: !!(message.Is_Delete ?? message.isDeleted ?? message.deleted),
  };
};

/** Snapshot of a message picked from the open conversation. */
export const buildRelatedMessage = (message, selectedChat) =>
  toRelatedMessage(message, message?.role === 'user' ? getCustomerName(selectedChat) : 'Agent');

const formatDuration = (seconds) => {
  const total = Math.max(0, Math.round(seconds));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

export const getRelatedMessagePreview = (relatedMessage) => {
  if (!relatedMessage) return '';
  if (relatedMessage.deleted) return 'This message was deleted';

  const { type, text, caption, file_name: fileName, duration } = relatedMessage;
  if (type === 'text') return text || 'Message';

  const label = MESSAGE_TYPE_LABELS[type] || 'Attachment';
  let detail = caption || text;
  if (!detail && type === 'voice' && duration) detail = formatDuration(duration);
  if (!detail && type === 'document' && fileName) detail = fileName;
  return detail ? `${label} · ${detail}` : label;
};

export const getRelatedMessageSender = (relatedMessage, customerName) =>
  relatedMessage?.sender_name || (relatedMessage?.is_customer ? customerName : 'Agent') || 'Unknown';

/* ------------------------------------------------------------------------------------------ */
/* Validation / errors                                                                        */
/* ------------------------------------------------------------------------------------------ */

export const validateFollowUpForm = (form, { requireFutureDate = true } = {}) => {
  const errors = {};
  if (!form.dueAt) {
    errors.dueAt = 'Date is required';
  } else if (requireFutureDate && new Date(form.dueAt) <= new Date()) {
    errors.dueAt = 'Date & time must be in the future';
  }
  if (!form.title.trim()) {
    errors.title = 'Title is required';
  } else if (form.title.trim().length > FOLLOW_UP_TITLE_MAX_LENGTH) {
    errors.title = `Title must be at most ${FOLLOW_UP_TITLE_MAX_LENGTH} characters`;
  }
  return errors;
};

const API_FIELD_TO_FORM_FIELD = {
  title: 'title',
  description: 'description',
  due_at: 'dueAt',
  dueAt: 'dueAt',
  message_id: 'relatedMessage',
  messageId: 'relatedMessage',
};

const firstMessage = (value) => {
  if (Array.isArray(value)) return firstMessage(value[0]);
  if (value && typeof value === 'object') return value.message || firstMessage(Object.values(value)[0]);
  return typeof value === 'string' ? value : null;
};

export const extractFollowUpFieldErrors = (error) => {
  const apiError = error?.response?.data?.error;
  const details = apiError?.details;
  if (!details || typeof details !== 'object') return {};

  return Object.entries(details).reduce((acc, [field, value]) => {
    const formField = API_FIELD_TO_FORM_FIELD[field];
    const message = firstMessage(value);
    if (formField && message) acc[formField] = message;
    return acc;
  }, {});
};
